import Image from 'components/Common/Image'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { rem } from 'polished'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import zIndex from 'styles/tools/z-index'
import type { FullTrip } from 'tripPlanner/types/common'
import { setCookie, getCookies } from 'lib/web/cookieUtils'
import { DurationSeconds } from 'lib/datetime/durationInSeconds'

const StickyContainer = styled.div`
  position: sticky;
  right: 0;
  bottom: ${props => props.theme.layout.footerFloatingOffset};
  left: 0;
  z-index: ${zIndex.floatingPanel};
  padding: 0 ${rem(20)} ${rem(20)};
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }

`

const Container = styled(Sheet)`
  max-width: ${rem(800)};
  flex-grow: 1;

`

const PROGRESS_BAR_HEIGHT = rem(12)

const progressFillAnimation = keyframes`
  0% {
    width: 0%;
  }

  60% {
    width: 80%;
  }

  85% {
    width: 90%;
  }

  100% {
    width: 95%;
  }
`

const ProgressBar = styled.div`
  grid-area: progress-bar;
  height: ${PROGRESS_BAR_HEIGHT};
  background-color: ${props => props.theme.palette.neutral.default.three};
`

const ProgressBarFill = styled.div`
  height: 100%;
  background-color: ${props => props.theme.palette.highlight.primary.normalForeground};
  animation: ${progressFillAnimation} 100s cubic-bezier(0.4, 0, 0.2, 1) forwards;
`

interface Props {
  trip: FullTrip
  isVisible: boolean
  generationStatus: string | undefined
}

function AITripLoadingStickyFooter({
  trip,
  isVisible,
  generationStatus,
}: Props) {
  const intl = useIntl()

  useEffect(() => {
    if (generationStatus === 'SUCCESS' && trip?.name && trip?.id) {
      const cookieName = `trip_success_snackbar_${trip.id}`
      const hasShownSnackbar = getCookies()[cookieName]
      if (!hasShownSnackbar) {
        showSnackbar(
          intl.formatMessage({
            id: 'cEmEDv7UMfIw4IfVsTk7',
            defaultMessage: 'Customise it however you like',
            description: 'Snackbar message shown when AI trip generation is complete',
          }),
          'success',
          {
            heading: intl.formatMessage({
              id: 'zqbbbiCkwGnM39eenksU',
              defaultMessage: 'Your trip is ready!',
              description: 'Snackbar heading shown when AI trip generation is complete',
            }),
            icon: <SolidSparklesIcon />,
          },
        )
        setCookie(cookieName, '1', DurationSeconds.TWO_YEARS, '/', 'Lax')
      }
    }
  }, [generationStatus, intl, trip?.name, trip?.id])

  if (!trip || !isVisible) {
    return null
  }
  return <StickyContainer>
    {generationStatus === 'IN_PROGRESS' && (
      <Group direction="horizontal" horizontalAlign="center">
        <Container
          border="neutral-five"
          background="neutral-eight"
        >
          <Group direction="vertical">
            <Group direction="horizontal" verticalAlign="center" horizontalAlign="center">
              <div>
                <Image
                  id="2nlwgew937u1cjqlbj98"
                  enhancedFormats={false}
                  width={68}
                  height={68}
                />
              </div>
              <HeadingTextBlock variant="heading5" colour="highlight-secondary">
                {intl.formatMessage({
                  id: '6GhYweqqmZ2RqIY-0Va3',
                  defaultMessage: 'Almost there! Finalising your itinerary...',
                  description: 'Loading message shown while AI trip itinerary is being finalised',
                })}
              </HeadingTextBlock>
            </Group>
            <ProgressBar>
              <ProgressBarFill />
            </ProgressBar>
          </Group>
        </Container>
      </Group>
    )}
  </StickyContainer>
}

export default AITripLoadingStickyFooter
